/**
 * ============================================================================
 * NewSimulationWizard — S19.9 + Fase 3.3 (HS Code + Benefit Aduanero)
 * ============================================================================
 * Tabs:
 *   1. Clonar Existente   → clonar_embarque_a_proforma RPC
 *   2. Crear Desde Cero   → useCreateGreenfieldSimulation con derivación
 *                            automática de Arancel/IVA vía HS Code +
 *                            BenefitAduaneroSelect.
 *   3. Desde PDF          → IA extractor.
 * ============================================================================
 */

import { useEffect, useState } from "react";
import { Copy, FileText, HelpCircle, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";

import { EmbarqueCombobox } from "@/components/admin/EmbarqueCombobox";
import { useClonarEmbarqueAProforma } from "@/hooks/useProformaSimulations";
import {
  useCreateGreenfieldSimulation,
  useExtractQuoteSimulation,
  type GreenfieldFormInput,
} from "@/hooks/useCreateGreenfieldSimulation";
import { FileDropzone, type DropzoneState } from "@/components/ui/FileDropzone";
import { useTRM } from "@/hooks/useTRM";
import { CurrencyCombobox } from "@/components/CurrencyCombobox";

interface NewSimulationWizardProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (newId: string) => void;
  /** Modo de UI: en "quick" se ocultan campos avanzados (ICO, etc.). */
  uiMode?: import("@/lib/simulatorUiMode").UiMode;
}


function InfoTip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function NewSimulationWizard({
  open,
  onOpenChange,
  onCreated,
  uiMode = "quick",
}: NewSimulationWizardProps) {
  const { toast } = useToast();
  const { trm: trmHoy, trmLoading } = useTRM();

  const [tab, setTab] = useState<"clone" | "greenfield" | "pdf">("clone");

  // ── Clonar ──────────────────────────────────────────────
  const [embarqueId, setEmbarqueId] = useState<string | null>(null);
  const [cloneNombre, setCloneNombre] = useState("");
  const clonar = useClonarEmbarqueAProforma();

  // ── Greenfield ──────────────────────────────────────────
  const [gfNombre, setGfNombre] = useState("");
  const [gfTrm, setGfTrm] = useState<number>(0);
  const [gfTrmTouched, setGfTrmTouched] = useState(false);
  const [gfMoneda, setGfMoneda] = useState<string>("USD");
  const greenfield = useCreateGreenfieldSimulation();

  // ── PDF Extraction ──────────────────────────────────────
  const [pdfNombre, setPdfNombre] = useState("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfState, setPdfState] = useState<DropzoneState>("idle");
  const [pdfError, setPdfError] = useState<string | null>(null);
  const extractQuote = useExtractQuoteSimulation();

  // Precarga TRM cuando carga el hook (sólo si el usuario no la editó).
  useEffect(() => {
    if (!gfTrmTouched && trmHoy && trmHoy > 0) {
      setGfTrm(trmHoy);
    }
  }, [trmHoy, gfTrmTouched]);

  // Reset al cerrar
  useEffect(() => {
    if (!open) {
      setTab("clone");
      setEmbarqueId(null);
      setCloneNombre("");
      setGfNombre("");
      setGfTrm(trmHoy && trmHoy > 0 ? trmHoy : 4000);
      setGfTrmTouched(false);
      setGfMoneda("USD");
      setPdfNombre("");
      setPdfFile(null);
      setPdfState("idle");
      setPdfError(null);
    }
  }, [open, trmHoy]);

  const handleClone = async () => {
    if (!embarqueId) {
      toast({ title: "Selecciona un embarque", variant: "destructive" });
      return;
    }
    try {
      const newId = await clonar.mutateAsync({
        embarqueId,
        nombre: cloneNombre.trim() || undefined,
      });
      toast({
        title: "Simulación creada",
        description: "Ajusta los parámetros con los sliders.",
      });
      onOpenChange(false);
      onCreated(newId);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Error desconocido";
      toast({ title: "No se pudo clonar", description: msg, variant: "destructive" });
    }
  };

  const handleGreenfield = async () => {
    if (!gfNombre.trim()) {
      toast({ title: "Falta nombre de la simulación", variant: "destructive" });
      return;
    }
    const input: GreenfieldFormInput = {
      nombre: gfNombre,
      trmProyectada: gfTrm,
      moneda: gfMoneda,
    };
    try {
      const newId = await greenfield.mutateAsync(input);
      toast({
        title: "Simulación creada",
        description: "Añade ítems y gastos desde el panel predictivo.",
      });
      onOpenChange(false);
      onCreated(newId);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Error desconocido";
      toast({ title: "No se pudo crear", description: msg, variant: "destructive" });
    }
  };

  const handlePdfFile = async (file: File) => {
    setPdfFile(file);
    setPdfError(null);
    setPdfState("uploading");
    try {
      const { simulationId, extracted } = await extractQuote.mutateAsync({
        file,
        nombre: pdfNombre.trim() || undefined,
      });
      setPdfState("success");
      toast({
        title: "Cotización procesada",
        description: `${extracted.items.length} ítem(s) y ${extracted.gastos.length} gasto(s) detectados.`,
      });
      onOpenChange(false);
      onCreated(simulationId);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Error desconocido";
      setPdfError(msg);
      setPdfState("error");
      toast({
        title: "No se pudo procesar el PDF",
        description: msg,
        variant: "destructive",
      });
    }
  };

  const resetPdf = () => {
    setPdfFile(null);
    setPdfError(null);
    setPdfState("idle");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nueva simulación pro-forma</DialogTitle>
          <DialogDescription>
            Clona un embarque real, construye un escenario hipotético o sube una
            cotización en PDF para extracción automática.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="w-full">
          <TabsList className="grid w-full grid-cols-3 border-b">
            <TabsTrigger value="clone" className="gap-2">
              <Copy className="h-3.5 w-3.5" />
              Clonar
            </TabsTrigger>
            <TabsTrigger value="greenfield" className="gap-2">
              <Sparkles className="h-3.5 w-3.5" />
              Desde Cero
            </TabsTrigger>
            <TabsTrigger value="pdf" className="gap-2">
              <FileText className="h-3.5 w-3.5" />
              Desde PDF
            </TabsTrigger>
          </TabsList>

          {/* ── Clonar ── */}
          <TabsContent value="clone" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Embarque base</Label>
              <EmbarqueCombobox
                value={embarqueId}
                onChange={setEmbarqueId}
                placeholder="Buscar embarque..."
              />
              <p className="text-xs text-muted-foreground">
                Hereda gastos, ítems, TRM y reglas de cumplimiento del embarque seleccionado.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Nombre (opcional)</Label>
              <Input
                value={cloneNombre}
                onChange={(e) => setCloneNombre(e.target.value)}
                placeholder="Ej. Escenario Q3 con TRM al alza"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={handleClone} disabled={clonar.isPending || !embarqueId}>
                {clonar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Clonar simulación
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* ── Greenfield ── */}
          <TabsContent value="greenfield" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label>Nombre de la simulación</Label>
                <Input
                  value={gfNombre}
                  onChange={(e) => setGfNombre(e.target.value)}
                  placeholder="Ej. Proyecto Llantas Asia"
                />
              </div>

              <div className="space-y-2 col-span-2">
                <div className="flex items-center gap-1.5">
                  <Label>TRM Proyectada (COP por USD)</Label>
                  <InfoTip text="Tasa de cambio del día (USD→COP). Modifícala para simular escenarios con devaluación o revaluación." />
                  {trmLoading && (
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  )}
                </div>
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  value={gfTrm || ""}
                  onChange={(e) => {
                    setGfTrm(Number(e.target.value) || 0);
                    setGfTrmTouched(true);
                  }}
                  placeholder={trmLoading ? "Cargando TRM…" : "4000"}
                  className="tabular-nums"
                />
                {trmHoy && !gfTrmTouched && (
                  <p className="text-[11px] text-muted-foreground">
                    Precargado con TRM oficial vigente: {trmHoy.toLocaleString("es-CO")} COP/USD
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label>Moneda principal</Label>
                <InfoTip text="Moneda predominante de la simulación. Podrás añadir ítems en otras monedas luego." />
              </div>
              <CurrencyCombobox value={gfMoneda} onChange={setGfMoneda} />
            </div>

            <p className="text-xs text-muted-foreground">
              La simulación se creará vacía. Añade ítems y gastos manualmente desde el panel predictivo.
            </p>

            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleGreenfield}
                disabled={greenfield.isPending || !gfNombre.trim()}
              >
                {greenfield.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Crear simulación
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* ── Desde PDF ── */}
          <TabsContent value="pdf" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Nombre de la simulación (opcional)</Label>
              <Input
                value={pdfNombre}
                onChange={(e) => setPdfNombre(e.target.value)}
                placeholder="Si lo omites, usaremos el proveedor + número"
                disabled={pdfState === "uploading"}
              />
              <p className="text-xs text-muted-foreground">
                Sube una cotización en PDF y la IA extraerá ítems, precios y gastos
                logísticos sugeridos. La simulación se creará automáticamente.
              </p>
            </div>

            <FileDropzone
              accept=".pdf,application/pdf,image/*"
              maxSizeMb={20}
              state={pdfState}
              fileName={pdfFile?.name}
              onFileSelected={handlePdfFile}
              onReset={resetPdf}
              successMessage="Cotización procesada — abriendo simulación…"
              errorMessage={pdfError ?? "Error al procesar la cotización"}
              idleTitle="Arrastra tu PDF de cotización"
              idleSubtitle="o haz clic para seleccionar un archivo"
              hint="PDF o imagen — máx. 20 MB. Procesado por IA (~30-60s)."
              disabled={pdfState === "uploading"}
            />

            <DialogFooter className="pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pdfState === "uploading"}
              >
                Cancelar
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
